import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth } from "./useAuth";
import {
    useStartImpersonationMutation,
    useStopImpersonationMutation,
    useReturnToAdminMutation,
    useGetProfileMutation,
    IImpersonationResponse,
} from "../services/nestjs-base-api";
import nestjsBaseApi from "../services/nestjs-base-api";

interface ImpersonationContextType {
    isImpersonating: boolean;
    impersonationData: IImpersonationResponse | null;
    startImpersonation: (organizationId: number, userId: number, reason: string, previewPlan?: string) => Promise<void>;
    stopImpersonation: () => Promise<void>;
    returnToAdmin: () => Promise<void>;
    isLoading: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
    isImpersonating: false,
    impersonationData: null,
    startImpersonation: async () => {},
    stopImpersonation: async () => {},
    returnToAdmin: async () => {},
    isLoading: false,
});

export const ImpersonationProvider = ({ children }: { children: React.ReactNode }) => {
    const { user, login, logout } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [impersonationData, setImpersonationData] = useState<IImpersonationResponse | null>(null);

    const [startImpersonationMutation, { isLoading: isStartingImpersonation }] = useStartImpersonationMutation();
    const [stopImpersonationMutation, { isLoading: isStoppingImpersonation }] = useStopImpersonationMutation();
    const [returnToAdminMutation, { isLoading: isReturningToAdmin }] = useReturnToAdminMutation();
    const [getProfile] = useGetProfileMutation();

    const isLoading = isStartingImpersonation || isStoppingImpersonation || isReturningToAdmin;

    // Check if we're currently impersonating by looking at the stored user data
    const isImpersonating = useMemo(() => {
        return !!(user?.impersonation || impersonationData);
    }, [user, impersonationData]);

    const startImpersonation = useCallback(async (organizationId: number, userId: number, reason: string, previewPlan?: string) => {
        if (!user?.jwtToken) {
            message.error('Authentication required');
            return;
        }

        try {
            const response = await startImpersonationMutation({
                jwtToken: user.jwtToken,
                organizationId,
                payload: { userId, reason, previewPlan }
            }).unwrap();

            // Store impersonation data
            setImpersonationData(response);

            // Swap the stored token FIRST: baseQuery's prepareHeaders always
            // rebuilds the Authorization header from localStorage, so the
            // jwtToken argument below is ignored and the profile call would come
            // back as the admin's own profile if the admin token were still here.
            // Deliberately NOT resetting the RTK cache yet — that would refetch
            // every subscribed query on the still-mounted admin page under the
            // impersonation token and blow up with permission errors.
            localStorage.setItem('token', response.access_token);

            // Load the target user's real profile with the impersonation token —
            // the same call SigninPage makes. target_user only carries id/name for
            // the organisation, so building the user from it alone leaves fields
            // like organisation.templateType undefined and the impersonated
            // session sees a different app than the real user does (e.g. the
            // onboarding wizard re-asking for business model and product type).
            const profileResponse = await getProfile({ jwtToken: response.access_token });
            const profile = 'data' in profileResponse ? profileResponse.data : null;
            if (!profile) {
                // Not fatal — impersonation still works off target_user, but the
                // session will be missing org fields, so make the gap visible.
                console.warn('Impersonation: profile fetch failed, falling back to target_user');
            }

            // Update the auth context with the new token and user info
            const impersonatedUser = {
                ...(profile ?? {}),
                jwtToken: response.access_token,
                id: response.target_user.id,
                email: response.target_user.email,
                firstName: response.target_user.firstName,
                lastName: response.target_user.lastName,
                roles: response.target_user.roles,
                needsOnboarding: response.needsOnboarding,
                // Fall back to the impersonation payload only if the profile call failed.
                organisation: profile?.organisation ?? {
                    id: response.target_user.organisationId,
                    name: response.target_user.organisationName,
                },
                impersonation: response.impersonation,
                // Keep reference to original admin user
                originalAdminUser: {
                    id: user.id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    roles: user.roles,
                    organisation: user.organisation,
                }
            };

            // Reset RTK Query cache to clear admin user's data before switching
            // context, immediately before the auth context flips so nothing
            // refetches against a half-switched session.
            dispatch(nestjsBaseApi.util.resetApiState());

            // This will update the auth context with the impersonated user
            login(impersonatedUser);

            message.success(
                `Now impersonating ${response.target_user.firstName} ${response.target_user.lastName} (${response.target_user.email})`,
                5
            );
        } catch (error: any) {
            // If we failed after swapping the token, put the admin's back —
            // otherwise the auth context still says admin while every request
            // goes out as the impersonated user.
            const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
            if (!currentUser?.impersonation && user?.jwtToken) {
                localStorage.setItem('token', user.jwtToken);
            }
            console.error('Failed to start impersonation:', error);
            message.error(error?.data?.message || 'Failed to start impersonation');
        }
    }, [user, startImpersonationMutation, getProfile, dispatch, login]);

    const stopImpersonation = useCallback(async () => {
        if (!user?.jwtToken) {
            message.error('Authentication required');
            return;
        }

        try {
            await stopImpersonationMutation({
                jwtToken: user.jwtToken
            }).unwrap();

            // Clear impersonation data
            setImpersonationData(null);
            
            message.success('Impersonation ended - logging out');
            
            // Log out completely after a short delay to show the message
            setTimeout(() => {
                logout();
            }, 1500);
        } catch (error: any) {
            console.error('Failed to stop impersonation:', error);
            message.error(error?.data?.message || 'Failed to stop impersonation');
        }
    }, [user, stopImpersonationMutation, logout]);

    const returnToAdmin = useCallback(async () => {
        if (!user?.jwtToken) {
            message.error('Authentication required');
            return;
        }

        try {
            const response = await returnToAdminMutation({
                jwtToken: user.jwtToken
            }).unwrap();

            // Clear impersonation data
            setImpersonationData(null);

            // Restore original admin user with new token
            const originalAdminUser = {
                ...user.originalAdminUser,
                jwtToken: response.access_token,
                // Ensure organisation is included if it exists
                organisation: user.originalAdminUser.organisation,
            };

            // Store the admin token separately for API calls
            localStorage.setItem('token', response.access_token);

            // Update auth context with original admin user
            login(originalAdminUser);

            // Reset RTK Query cache to clear impersonated user's data
            dispatch(nestjsBaseApi.util.resetApiState());

            message.success('Returned to admin view');

            // Navigate to admin organizations page
            navigate('/admin/organizations');
        } catch (error: any) {
            console.error('Failed to return to admin:', error);
            message.error(error?.data?.message || 'Failed to return to admin');
        }
    }, [user, returnToAdminMutation, login, dispatch, navigate]);

    const value = useMemo(() => ({
        isImpersonating,
        impersonationData: impersonationData || (user?.impersonation ? {
            access_token: user.jwtToken,
            impersonation: user.impersonation,
            target_user: {
                id: user.id,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                roles: user.roles,
                organisationId: user.organisation?.id,
                organisationName: user.organisation?.name,
            }
        } as IImpersonationResponse : null),
        startImpersonation,
        stopImpersonation,
        returnToAdmin,
        isLoading,
    }), [
        isImpersonating,
        impersonationData,
        user,
        startImpersonation,
        stopImpersonation,
        returnToAdmin,
        isLoading,
    ]);

    return (
        <ImpersonationContext.Provider value={value}>
            {children}
        </ImpersonationContext.Provider>
    );
};

export const useImpersonation = () => {
    const context = useContext(ImpersonationContext);
    if (!context) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
};